import { z } from 'zod'
import { speechModeSchema } from './speech'

/** Hosts where test mode must never run. Test mode is only allowed on local hosts. */
export const PRODUCTION_HOSTS = ['hugo.beyonityhello.com'] as const

const LOCAL_HOST = /^(localhost|127\.0\.0\.1|\[::1\]|.+\.localhost|.+\.test)$/

const flag = z
  .enum(['true', 'false', '1', '0', ''])
  .optional()
  .transform((v) => v === 'true' || v === '1')

const csvLower = (fallback = '') =>
  z
    .string()
    .optional()
    .transform((v) =>
      (v ?? fallback)
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean),
    )

const optionalNonEmpty = z
  .string()
  .optional()
  .transform((v) => (v && v.trim() !== '' ? v.trim() : undefined))

/** Variables that point hugo at fake services. Only allowed in test mode (see below). */
export const TEST_ONLY_VARIABLES = ['GOOGLE_API_BASE_URL', 'GITHUB_API_BASE_URL'] as const

/**
 * How hugo talks to Claude (packages/ai): `off`; `fake` (deterministic answers, no network);
 * `replay` (recorded answers); `record` (calls Claude and records the answers); `live`.
 */
export const aiModes = ['off', 'fake', 'replay', 'record', 'live'] as const
export const aiModeSchema = z.enum(aiModes)
export type AiMode = z.output<typeof aiModeSchema>

/** AI modes that call Claude: they need ANTHROPIC_API_KEY. */
export const AI_LIVE_MODES = ['live', 'record'] as const satisfies readonly AiMode[]
/** AI modes whose answers are not Claude's: test mode or outside production only. */
export const AI_TEST_MODES = ['fake', 'replay'] as const satisfies readonly AiMode[]

const aiModelId = (fallback: string) =>
  optionalNonEmpty.pipe(
    z
      .string()
      .max(200)
      .regex(/^[\w.:/@-]+$/, 'must be a model id')
      .default(fallback),
  )

export const serverEnvSchema = z.object({
  NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
  PORT: z.coerce.number().int().positive().default(3000),
  LOG_LEVEL: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace', 'silent']).default('info'),
  APP_VERSION: z.string().min(1).default('dev'),
  /** Browser-facing origin of the app, e.g. https://hugo.beyonityhello.com or http://localhost:8080 */
  PUBLIC_URL: z.url({ protocol: /^https?$/ }).transform((v) => v.replace(/\/+$/, '')),
  DATABASE_URL: z.string().min(1),
  BETTER_AUTH_SECRET: z.string().min(32, 'must be at least 32 characters'),
  GOOGLE_CLIENT_ID: optionalNonEmpty,
  GOOGLE_CLIENT_SECRET: optionalNonEmpty,
  /** Optional Google Workspace domain hint/enforcement (`hd`). */
  GOOGLE_HOSTED_DOMAIN: optionalNonEmpty,
  /** Who may sign in. Empty lists mean nobody can sign in with Google (fail closed). */
  ALLOWED_EMAILS: csvLower(),
  ALLOWED_DOMAINS: csvLower(),
  /** WebView origins of the native apps (iOS, Android). */
  NATIVE_ORIGINS: csvLower('capacitor://localhost,https://localhost'),
  HUGO_TEST_MODE: flag,
  HUGO_WRITES_ENABLED: flag,
  /**
   * Base URL for Google API calls instead of Google itself: the fake Google service of the test
   * stack. Test mode only, otherwise users' Google tokens would be sent to another host.
   */
  GOOGLE_API_BASE_URL: optionalNonEmpty.pipe(
    z
      .url({ protocol: /^https?$/ })
      .transform((v) => v.replace(/\/+$/, ''))
      .optional(),
  ),
  /** The GitHub OAuth App hugo connects GitHub with (decision 9). Both or neither. */
  GITHUB_CLIENT_ID: optionalNonEmpty,
  GITHUB_CLIENT_SECRET: optionalNonEmpty,
  /**
   * Base URL for GitHub API calls instead of api.github.com: the fake GitHub of the test stack.
   * Test mode only (see TEST_ONLY_VARIABLES).
   */
  GITHUB_API_BASE_URL: optionalNonEmpty.pipe(
    z
      .url({ protocol: /^https?$/ })
      .transform((v) => v.replace(/\/+$/, ''))
      .optional(),
  ),
  /** Shared secret that protects /api/webhooks/* (e.g. the Gmail push subscription). */
  WEBHOOK_TOKEN: optionalNonEmpty.pipe(
    z.string().min(24, 'must be at least 24 characters').optional(),
  ),
  /** Pub/Sub topic Gmail push notifications go to: projects/<project>/topics/<topic>. */
  GOOGLE_PUBSUB_TOPIC: optionalNonEmpty,
  /** See `aiModes`. `live` and `record` need ANTHROPIC_API_KEY; `fake` and `replay` test mode. */
  AI_MODE: optionalNonEmpty.pipe(aiModeSchema.default('off')),
  /** Secret. Only packages/ai uses it. */
  ANTHROPIC_API_KEY: optionalNonEmpty,
  /** Where `replay` reads and `record` writes answers. Unset: the caller's default. */
  AI_FIXTURES_DIR: optionalNonEmpty,
  /** Most the AI may cost per day, in USD; callers stop calling it beyond this. */
  AI_DAILY_BUDGET_USD: optionalNonEmpty.pipe(z.coerce.number<string>().min(0).default(2)),
  /** Model for triage suggestions (many small calls). */
  AI_MODEL_TRIAGE: aiModelId('claude-haiku-5-5'),
  /** Model for reply drafts. */
  AI_MODEL_DRAFT: aiModelId('claude-sonnet-5-5'),
  /** See `speechModes`. `fake` needs SPEECH_FAKE_URL and SPEECH_FAKE_SECRET, and test mode. */
  SPEECH_MODE: optionalNonEmpty.pipe(speechModeSchema.default('off')),
  /** Where browsers reach the fake speech service (ws:// or wss://), e.g. on the test stack. */
  SPEECH_FAKE_URL: optionalNonEmpty.pipe(z.url({ protocol: /^wss?$/ }).optional()),
  /** Secret shared with the fake speech service: it signs the short-lived session tokens. */
  SPEECH_FAKE_SECRET: optionalNonEmpty.pipe(
    z.string().min(32, 'must be at least 32 characters').optional(),
  ),
})

export type ServerEnv = z.output<typeof serverEnvSchema>

/** The AI variables alone, with what decides whether test-only AI modes may run. */
export const aiEnvSchema = serverEnvSchema.pick({
  NODE_ENV: true,
  HUGO_TEST_MODE: true,
  AI_MODE: true,
  ANTHROPIC_API_KEY: true,
  AI_FIXTURES_DIR: true,
  AI_DAILY_BUDGET_USD: true,
  AI_MODEL_TRIAGE: true,
  AI_MODEL_DRAFT: true,
})

export type AiEnv = z.output<typeof aiEnvSchema>

export class EnvError extends Error {
  constructor(readonly problems: string[]) {
    super(`Invalid environment:\n${problems.map((p) => `  - ${p}`).join('\n')}`)
    this.name = 'EnvError'
  }
}

/**
 * Parse and validate the server environment. Error messages name the variable and the problem,
 * never the value, so secrets cannot leak into logs.
 */
export function loadServerEnv(source: Record<string, string | undefined> = process.env): ServerEnv {
  const parsed = serverEnvSchema.safeParse(source)
  if (!parsed.success) throw new EnvError(describeIssues(parsed.error))
  const env = parsed.data
  assertTestModeAllowed(env)
  assertTestOnlyVariables(env)
  assertAiModeAllowed(env)
  assertSpeechModeAllowed(env)
  assertPairs(env)
  return env
}

/** Settings that only work together: with one of them, the other is missing (names only). */
const PAIRS = [['GITHUB_CLIENT_ID', 'GITHUB_CLIENT_SECRET']] as const

function assertPairs(env: ServerEnv) {
  for (const [a, b] of PAIRS) {
    const missing = env[a] === undefined ? a : env[b] === undefined ? b : null
    if (missing && (env[a] !== undefined || env[b] !== undefined)) {
      throw new EnvError([`${missing}: required together with ${missing === a ? b : a}`])
    }
  }
}

/**
 * Parse and validate only the AI variables, for tools that talk to Claude without the server
 * (the eval runner). Same rules and the same secret-free messages as `loadServerEnv`.
 */
export function loadAiEnv(source: Record<string, string | undefined> = process.env): AiEnv {
  const parsed = aiEnvSchema.safeParse(source)
  if (!parsed.success) throw new EnvError(describeIssues(parsed.error))
  assertAiModeAllowed(parsed.data)
  return parsed.data
}

function describeIssues(error: z.ZodError): string[] {
  return error.issues.map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
}

/**
 * `live` and `record` call Claude, so they need a key. `fake` and `replay` answers are not
 * Claude's: test mode or outside production only, so they never reach real users. Names the
 * variables, never their values.
 */
export function assertAiModeAllowed(
  env: Pick<ServerEnv, 'NODE_ENV' | 'HUGO_TEST_MODE' | 'AI_MODE' | 'ANTHROPIC_API_KEY'>,
) {
  const problems: string[] = []
  const mode = env.AI_MODE
  if ((AI_LIVE_MODES as readonly AiMode[]).includes(mode) && !env.ANTHROPIC_API_KEY) {
    problems.push(`ANTHROPIC_API_KEY: required when AI_MODE is ${mode}`)
  }
  if (
    (AI_TEST_MODES as readonly AiMode[]).includes(mode) &&
    env.NODE_ENV === 'production' &&
    !env.HUGO_TEST_MODE
  ) {
    problems.push(
      `AI_MODE: ${mode} is only allowed in test mode (HUGO_TEST_MODE) or outside production; its answers must never reach real users`,
    )
  }
  if (problems.length > 0) throw new EnvError(problems)
}

/**
 * The fake speech service answers with scripted transcripts: test mode or outside production
 * only, and it needs where browsers reach it and the secret it shares with hugo. Names the
 * variables, never their values.
 */
export function assertSpeechModeAllowed(
  env: Pick<
    ServerEnv,
    'NODE_ENV' | 'HUGO_TEST_MODE' | 'SPEECH_MODE' | 'SPEECH_FAKE_URL' | 'SPEECH_FAKE_SECRET'
  >,
) {
  if (env.SPEECH_MODE !== 'fake') return
  const problems: string[] = []
  if (env.NODE_ENV === 'production' && !env.HUGO_TEST_MODE) {
    problems.push(
      'SPEECH_MODE: fake is only allowed in test mode (HUGO_TEST_MODE) or outside production; its transcripts must never reach real users',
    )
  }
  if (!env.SPEECH_FAKE_URL) problems.push('SPEECH_FAKE_URL: required when SPEECH_MODE is fake')
  if (!env.SPEECH_FAKE_SECRET) {
    problems.push('SPEECH_FAKE_SECRET: required when SPEECH_MODE is fake')
  }
  if (problems.length > 0) throw new EnvError(problems)
}

/**
 * Variables that redirect calls to fake services may only be set in test mode: in production
 * they would send users' OAuth tokens to another host. Names the variable, never its value.
 */
export function assertTestOnlyVariables(
  env: Pick<ServerEnv, 'HUGO_TEST_MODE' | (typeof TEST_ONLY_VARIABLES)[number]>,
) {
  if (env.HUGO_TEST_MODE) return
  const set = TEST_ONLY_VARIABLES.filter((name) => env[name] !== undefined)
  if (set.length > 0) {
    throw new EnvError(
      set.map(
        (name) =>
          `${name}: only allowed in test mode (HUGO_TEST_MODE); it would send OAuth tokens to another host`,
      ),
    )
  }
}

/** Test mode exposes test-only routes. It may only run on local hosts, never on production. */
export function assertTestModeAllowed(env: Pick<ServerEnv, 'HUGO_TEST_MODE' | 'PUBLIC_URL'>) {
  if (!env.HUGO_TEST_MODE) return
  const host = new URL(env.PUBLIC_URL).hostname
  if ((PRODUCTION_HOSTS as readonly string[]).includes(host) || !LOCAL_HOST.test(host)) {
    throw new EnvError([
      `HUGO_TEST_MODE: test mode is only allowed on local hosts, not on "${host}"`,
    ])
  }
}

/** Whether an email may sign in, given the allowlists. Fails closed when both lists are empty. */
export function isEmailAllowed(
  email: string,
  allow: { emails: readonly string[]; domains: readonly string[] },
): boolean {
  const normalized = email.trim().toLowerCase()
  const domain = normalized.split('@')[1] ?? ''
  return allow.emails.includes(normalized) || (domain !== '' && allow.domains.includes(domain))
}
